import { Flex, IconEqualizer } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext from 'src/AppContext';
import useSort from 'src/customHooks/useSort';
import { COLORCARD, hasAdditionalSettingsModule, ModuleId, PS_EDITIONS as photoShop_Editions, SettingsModuleId } from 'src/graphql/mappers/module';
import styled from 'styled-components';
import FormElementWrapper from '../SelectElementWrapper';
import ModuleSelector, { SingleModule } from './ModuleSelector';
import ModuleSelectorWithOptions from './ModuleSelectorWithOptions';

export interface ProductsSectionProps {
    selectableModules: Array<SingleModule>;
    sectionTitle: string;
    onHandleModuleChange: (moduleId: ModuleId) => void;
    onSettingsIconClick: (moduleId: SettingsModuleId) => void;
    selectedModuleIds: Array<ModuleId>;
};

const ProductsSection: FunctionComponent<ProductsSectionProps> = (props) => {
    const { getSortFn } = useSort();
    const { t } = useTranslation();
    const { featureFlags } = useContext(AppContext);
    const photoShopModules = props.selectableModules.filter(m => photoShop_Editions.includes(m.id));
    const allOtherModules = props.selectableModules.filter(m => !photoShop_Editions.includes(m.id));

    const toBeDisplayedSortedModules = [
        {
            productName: 'GMG ColorPlugin for Adobe® Photoshop®',
            modules: [...photoShopModules].sort(getSortFn('name', 'ASC')),
        },
        ...allOtherModules.map(module => ({
            productName: module.name,
            modules: [module],
        })),
    ]
        .filter(product => product.modules.length > 0)
        .sort(getSortFn('productName', 'ASC'));

    // render is getting more complex,  think about to reduce conditional renderings
    return (
        <SectionWrapper label={props.sectionTitle} height="max-content">
            {toBeDisplayedSortedModules.map((module) => {
                const moduleWithMultipleOptions = module.modules.length > 1;
                if (moduleWithMultipleOptions) {
                    return <Flex flexDirection='column' key={module.productName}>
                        <ModuleSelectorWithOptions
                            moduleWithOptions={module}
                            onSelectModule={props.onHandleModuleChange}
                        />
                    </Flex>;
                } else {
                    const singleModule = module.modules[0];
                    return (
                        <Flex flexDirection='column' key={singleModule.id}>
                            <Flex alignItems='start' gap='20px'>
                                <ModuleSelector
                                    singleModule={singleModule}
                                    onSelectModule={props.onHandleModuleChange}
                                />
                                {hasAdditionalSettingsModule(singleModule) &&
                                    <SettingsIconContainer
                                        aria-disabled={!props.selectedModuleIds.includes(singleModule.id)}
                                        isDisabled={!props.selectedModuleIds.includes(singleModule.id)}
                                        aria-label={`settings icon for ${singleModule.name}`}
                                        onClick={() => {
                                            props.selectedModuleIds.includes(singleModule.id) && props.onSettingsIconClick(singleModule.id);
                                        }}
                                    >
                                        <IconEqualizer />
                                        <span>{t('AddOrEditUserModal.module_settings_action_link', 'Settings')}</span>
                                    </SettingsIconContainer>
                                }
                            </Flex>
                        </Flex>
                    );
                }
            })}
        </SectionWrapper>
    );
};

const SectionWrapper = styled(FormElementWrapper)`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SettingsIconContainer = styled(Flex) <{ isDisabled: boolean }>`
    align-items: flex-start;
    gap: 4px;
    margin-top: 3px;
    cursor: ${props => props.isDisabled ? 'default' : 'pointer'};
    color: ${props => props.isDisabled ? props.theme.colors.weak : props.theme.colors.highlight};
`;

export default ProductsSection;